import React, { useMemo } from 'react';
import { staffTrackLabel, trackKey } from '../../../../services/studentTracksModel';
import KeyControl from '../../../../components/Tracks/KeyControl';
import SaveKeyButton from '../../../../components/Tracks/SaveKeyButton';
import VocalsControl from '../tracks/VocalsControl';
import { currentMarker, sectionMarkersFor } from '../tracks/sectionMarkers';
import { timedLyricsOf } from '../stage/stageModel';
import { backTarget, forwardTarget, remaining, sectionLoop } from './nowPlayingModel';
import './NowPlayingBar.css';

/**
 * Now Playing (2026-10-29): the player pinned to the bottom of the lyrics,
 * so the words and the controls are one surface, docked in the Song panel
 * and in the detached window alike (the window portals this same pane).
 * Anthony: "combine the player and the scrolling lyrics … playback controls
 * pinned above or at the bottom. so it mimics the apple music app … as user
 * friendly as possible."
 *
 * Top to bottom, the music-app order: what is playing (song, then the file),
 * the scrub bar with time gone and time left, then Back · Play · Forward,
 * then the rehearsal tools (Track key + Save key, Loop section, Vocals).
 * Back / Forward move by section where the track has markers, else 10 s
 * (nowPlayingModel.js); each names where it goes UNDER the icon, so the
 * buttons never need a hover to explain themselves (keratoconus; touch).
 *
 * The track strip above the notes stays as the small player. This bar is
 * always about THIS song: with another song's track sounding, Play switches
 * to this one, and a line says so.
 *
 * Sticky to the bottom of whichever box scrolls the lyrics (the panel body,
 * the phone sheet, the window's mount), so nothing here has to know which
 * one it is in. Plain <button>s, each declaring its height (native.css).
 */
// `children` (2026-10-30): rendered at the bar's foot, inside the pinned panel;
// the detached window puts the song-note field there.
const NowPlayingBar = ({ song, file, files = [], player, onSaveKey = null, saveKeyWho = null, children = null }) => {
	const timed = useMemo(() => {
		const l = file ? timedLyricsOf(file, files) : null;
		return l ? l.timed : null;
	}, [file, files]);
	const markers = useMemo(() => (file ? sectionMarkersFor({ song, file, timed, files }) : []), [song, file, timed, files]);
	if (!song || !file || !player) return null;

	const cur = player.current;
	const loaded = Boolean(cur && cur.engine === 'audio' && cur.key === trackKey(file));
	const playing = loaded && player.isPlaying;
	const otherSounding = Boolean(cur && !loaded && player.isPlaying);
	const t = loaded ? Number(player.currentTime) || 0 : 0;
	const duration = loaded ? Number(player.duration) || 0 : 0;

	const title = song.metadata?.title || song.name || 'Untitled song';
	const lengthWord = file.length && file.length !== 'Full' ? file.length : null;
	const fileWords = [staffTrackLabel(file), lengthWord].filter(Boolean).join(' · ');

	const loop = player.loop || null;
	const canLoop = markers.length > 0 && typeof player.setLoop === 'function';
	const here = currentMarker(markers, t);
	const back = backTarget(markers, t);
	const fwd = forwardTarget(markers, t, duration);

	// While a section loops, moving section moves the loop with it; the
	// player puts the playhead at the new loop's start.
	const go = (target) => {
		if (!loaded || !target) return;
		if (loop && canLoop && target.marker) {
			const next = sectionLoop(markers, target.marker, duration);
			if (next) return player.setLoop(next);
		}
		if (loop) player.setLoop(null);
		player.seek(target.t);
	};
	const toggleLoop = () => {
		if (!loaded || !canLoop) return;
		if (loop) return player.setLoop(null);
		player.setLoop(sectionLoop(markers, here || markers[0], duration));
	};

	return (
		<div className={`ld-np${playing ? ' is-playing' : ''}`} role="region" aria-label="Now playing">
			<div className="ld-np__now">
				<p className="ld-np__title">{title}</p>
				<p className="ld-np__file">
					{fileWords}
					{loop && loaded && (
						<span className="ld-np__looptag">
							<i className="fa-sharp fa-solid fa-repeat" aria-hidden="true" />
							Looping {loop.label || 'a section'}
						</span>
					)}
				</p>
			</div>

			<div className="ld-np__seek">
				<input
					type="range"
					className="ld-np__range"
					min="0"
					max={duration > 0 ? duration : 0}
					step="0.1"
					value={duration > 0 ? t : 0}
					style={{ '--np-pct': `${duration > 0 ? Math.min(100, (t / duration) * 100) : 0}%` }}
					disabled={!loaded}
					aria-label="Seek"
					onChange={(e) => loaded && player.seek(parseFloat(e.target.value))}
				/>
				<div className="ld-np__times">
					<span>{player.formatTime(t)}</span>
					{here && <span className="ld-np__where">{here.label}</span>}
					<span>{remaining(t, duration, player.formatTime)}</span>
				</div>
			</div>

			<div className="ld-np__transport">
				<button type="button" className="ld-np__skip" aria-label={back.label} disabled={!loaded} onClick={() => go(back)}>
					<i className={`fa-sharp fa-solid ${markers.length ? 'fa-backward-step' : 'fa-rotate-left'}`} aria-hidden="true" />
					<span className="ld-np__skiplabel">{back.short}</span>
				</button>
				<button
					type="button"
					className="ld-np__play"
					aria-label={playing ? `Pause ${title}` : `Play ${title}`}
					onClick={() => player.toggle(file)}>
					<i className={`fa-sharp fa-solid ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
				</button>
				<button type="button" className="ld-np__skip" aria-label={fwd ? fwd.label : 'No later section'} disabled={!loaded || !fwd} onClick={() => go(fwd)}>
					<i className={`fa-sharp fa-solid ${markers.length ? 'fa-forward-step' : 'fa-rotate-right'}`} aria-hidden="true" />
					<span className="ld-np__skiplabel">{fwd ? fwd.short : 'End'}</span>
				</button>
			</div>

			{otherSounding && <p className="ld-np__note">Another song's track is playing. Play switches to this one.</p>}

			{/* ALWAYS RENDERED (2026-10-29, Anthony, looking at a paused bar: "so
			    how do we access the key change and stuff from this screen"). The
			    tools used to appear only once the track played, which hid them as
			    surely as a hover would. Before play they show, greyed by the
			    disabled fieldset, with a line saying what unlocks them. */}
			<fieldset className={`ld-np__tools${loaded ? '' : ' is-waiting'}`} disabled={!loaded}>
				<legend className="ld-np__legend">Rehearsal tools</legend>
				{/* Before play it reads Original: the hook's key belongs to whatever track was open last. */}
				<KeyControl player={loaded ? player : { ...player, semitones: 0 }} label="Track key" />
				{loaded && <SaveKeyButton files={files} player={player} onSaveKey={onSaveKey} who={saveKeyWho} />}
				<button
					type="button"
					className={`ld-np__tool${loop && loaded ? ' is-on' : ''}`}
					aria-pressed={Boolean(loop && loaded)}
					disabled={!canLoop}
					onClick={toggleLoop}>
					<i className="fa-sharp fa-solid fa-repeat" aria-hidden="true" />
					{loop && loaded ? 'Stop loop' : `Loop ${here ? here.label : 'section'}`}
				</button>
				<VocalsControl file={file} files={files} player={player} />
			</fieldset>
			{!loaded && <p className="ld-np__note">Press play to change the key, loop a section or bring the vocals in.</p>}
			{loaded && !canLoop && <p className="ld-np__note">Loop needs this track's sections: set them with Adjust in Tracks.</p>}

			{loaded && player.error && (
				<div className="ld-dismissable-error ld-np__error" role="alert">
					<p>{player.error}</p>
					{player.clearError && (
						<button type="button" className="ld-dismiss" onClick={player.clearError}>
							Dismiss
						</button>
					)}
				</div>
			)}
			{children}
		</div>
	);
};

export default NowPlayingBar;
